import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Users, HeartHandshake, ShoppingBag, Landmark, Brain, FileText, AlertTriangle, Building2, Heart, HardHat, Sparkles, Rocket, Globe, Target, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import HeroSection from "./HeroSection";
import CountUpNumber from "./CountUpNumber";
import usecaseCity from "@/assets/usecase-city.webp";
import usecaseHealthcare from "@/assets/usecase-healthcare.webp";
import usecaseInfra from "@/assets/usecase-infra.webp";
import logoFutureCities from "@/assets/logo-future-cities.webp";
import logoIfelseAi from "@/assets/logo-ifelse-ai.webp";
import safeTrackerCycle from "@/assets/safe-tracker-cycle.jpg";

interface LandingPageProps {
  onSignIn: () => void;
  onSignUp?: () => void;
}

export default function LandingPage({ onSignIn, onSignUp }: LandingPageProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="SeniorHub — Smart Age-Friendly Evaluation Platform"
        description="SAFE (Smart Age-Friendly Evaluation) — AI-powered assessment, certification, and ecosystem platform for age-friendly cities, care homes, and healthcare providers."
        path="/"
        keywords={["WHO age-friendly", "age-friendly cities framework", "smart city ageing", "elder care certification", "Gold Silver Bronze certification", "age-friendly KPIs", "UK age-friendly platform"]}
      />
      {/* 1. Hero */}
      <HeroSection onSignIn={onSignIn} onSignUp={onSignUp} />

      {/* 2. Mission & Vision */}
      <section id="platform-journey" className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#818CF8] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">Platform Vision</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              Building Age-Friendly Ecosystems<br />at Institutional Scale
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
              By 2050, over 2 billion people will be aged 60+. SeniorHub — powered by SeniorHub — provides a government-grade ecosystem platform to measure, implement, and transform age-friendly environments across cities, healthcare systems, and care infrastructure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Globe, title: "Global Need", desc: "Over 1 billion people globally lack access to age-friendly infrastructure and services." },
              { icon: Target, title: "Institutional Adoption", desc: "Designed for governments, care homes, hospitals, NGOs, and smart city programmes." },
              { icon: Award, title: "Age-Friendly Index", desc: "AI-powered benchmarking system for evaluating age-friendly communities across 10 dimensions." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="rounded-2xl bg-white/[0.06] backdrop-blur-[12px] border border-white/15 p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.15 }}
              >
                <item.icon className="text-white/70 mx-auto mb-3" size={32} strokeWidth={1.5} />
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. How It Works — Continuous Improvement Cycle */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <motion.h2
            className="text-3xl font-bold text-[#0F172A] text-center mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
          >
            How It Works
          </motion.h2>
          <motion.p
            className="text-[#64748B] text-center mb-12 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            A continuous age-friendliness improvement cycle — assess, identify, and re-measure.
          </motion.p>

          <motion.div
            className="relative rounded-2xl bg-white shadow-[0_10px_40px_-12px_rgba(15,23,42,0.15)] border border-[#E2E8F0] p-4 md:p-8"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src={safeTrackerCycle}
              alt="SeniorHub continuous age-friendliness improvement cycle: 1. Assess & Measure, 2. Identify & Optimize, 3. Transform & Re-measure"
              loading="lazy"
              decoding="async"
              className="w-full h-auto rounded-lg"
            />
          </motion.div>
        </div>
      </section>


      {/* 4. AI Intelligence */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #162033 100%)' }}>
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#6366F1]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-10 right-20 w-60 h-60 bg-[#3B82F6]/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#818CF8] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">AI-Powered</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                AI That Turns Data<br />Into Decisions
              </h2>
              <div className="space-y-5">
                {[
                  { icon: FileText, label: "Reports", text: "Auto-generated insights from assessment data", color: "#3B82F6" },
                  { icon: Brain, label: "Patterns", text: "Trend intelligence across all dimensions", color: "#8B5CF6" },
                  { icon: AlertTriangle, label: "Risk", text: "Early detection of critical gaps", color: "#F59E0B" },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.15 }}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}20` }}>
                      <item.icon size={20} style={{ color: item.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{item.label}</h4>
                      <p className="text-white/50 text-sm">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="rounded-2xl bg-white/[0.06] backdrop-blur-[12px] border border-white/10 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#EF4444]" />
                    <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
                    <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                  </div>
                  <span className="text-white/30 text-xs font-mono">AI Dashboard</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: "Overall Score", value: "78%", color: "#3B82F6" },
                    { label: "Risk Level", value: "Low", color: "#10B981" },
                    { label: "Insights", value: "24", color: "#8B5CF6" },
                  ].map((stat) => (
                    <motion.div
                      key={stat.label}
                      className="rounded-xl bg-white/[0.05] border border-white/[0.08] p-3 text-center"
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.5 }}
                    >
                      <div className="text-xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-white/40 text-[10px] mt-0.5">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Transport", pct: 85, color: "#3B82F6" },
                    { label: "Housing", pct: 62, color: "#8B5CF6" },
                    { label: "Social", pct: 91, color: "#10B981" },
                    { label: "Health", pct: 74, color: "#6366F1" },
                  ].map((bar, i) => (
                    <div key={bar.label} className="flex items-center gap-3">
                      <span className="text-white/40 text-[10px] w-16 text-right">{bar.label}</span>
                      <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: bar.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${bar.pct}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.6 + i * 0.1 }}
                        />
                      </div>
                      <span className="text-white/30 text-[10px] w-8">{bar.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -inset-4 bg-[#6366F1]/5 rounded-3xl blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. Use Cases */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
          >
            Designed for Every Age-Friendly Ecosystem
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {([
              { icon: Building2, title: "City & Municipality", description: "Urban planning and age-friendly city evaluations", img: usecaseCity },
              { icon: Heart, title: "Healthcare", description: "Hospital and care facility readiness assessments", img: usecaseHealthcare },
              { icon: HardHat, title: "Infrastructure", description: "Transport, housing, and public space accessibility", img: usecaseInfra },
            ] as const).map((uc, i) => (
              <motion.div
                key={uc.title}
                className="group relative rounded-2xl overflow-hidden h-64 cursor-pointer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <img src={uc.img} alt={uc.title} className="absolute inset-0 w-full h-full object-cover transition-all duration-500 blur-[2px] group-hover:blur-0 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/70 group-hover:via-black/30 transition-all duration-500" />
                <div className="absolute inset-0 flex flex-col items-center justify-end p-6 text-center z-10">
                  <uc.icon className="text-white/80 mb-3 group-hover:text-white transition-colors duration-300" size={36} strokeWidth={1.5} />
                  <h3 className="text-lg font-bold text-white">{uc.title}</h3>
                  <p className="text-white/60 text-sm mt-1">{uc.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Trust / Partners */}
      <section className="relative py-28 px-6 overflow-hidden" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">Trusted Worldwide</h2>
            <p className="text-[#64748B] text-lg max-w-xl mx-auto">Trusted by governments, institutions, and global partners.</p>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-center justify-center gap-10 md:gap-14 mt-14 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            {[
              { src: logoFutureCities, alt: "Future Cities & Assistive Technology Lab" },
              { src: logoIfelseAi, alt: "If Else AI" },
              { alt: "Smart City Alliance", placeholder: true },
              { alt: "WHO Age-Friendly", placeholder: true },
              { alt: "Urban Innovation Hub", placeholder: true },
            ].map((logo, i) => (
              <motion.div key={logo.alt} className="group" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}>
                {logo.placeholder ? (
                  <div className="h-12 w-28 rounded-lg bg-[#CBD5E1]/40 flex items-center justify-center grayscale opacity-50 group-hover:opacity-80 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-300">
                    <span className="text-[10px] font-medium text-[#64748B] text-center leading-tight px-2">{logo.alt}</span>
                  </div>
                ) : (
                  <img src={logo.src} alt={logo.alt} width={112} height={56} className="h-14 w-auto object-contain rounded-lg grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300" loading="lazy" decoding="async" />
                )}
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {[
              { end: 15, suffix: "+", label: "Institutions" },
              { end: 3, suffix: "", label: "Countries (Pilot Stage)" },
              { end: 120, suffix: "+", label: "Assessments Completed" },
            ].map((metric) => (
              <div key={metric.label} className="flex flex-col items-center">
                <span className="text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight">
                  <CountUpNumber end={metric.end} suffix={metric.suffix} />
                </span>
                <span className="text-[#64748B] text-sm font-medium mt-2">{metric.label}</span>
              </div>
            ))}
          </motion.div>

          <div className="w-16 h-[2px] bg-[#CBD5E1] mx-auto mb-10" />
          <motion.p
            className="text-[#334155] text-lg md:text-xl font-medium max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Empowering governments, institutions, and communities to build inclusive, age-friendly ecosystems.
          </motion.p>
        </div>
      </section>

      {/* 7. Why Create an Account */}
      <section className="relative py-24 px-6 overflow-hidden" style={{ background: 'linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">New here? Create an account to unlock full features</h2>
            <p className="text-[#64748B] text-lg max-w-xl mx-auto mb-12">Browse freely, sign up when you're ready to take action.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {[
              { icon: ShoppingBag, title: "Save & Compare", desc: "Shortlist solutions and compare vendors side by side." },
              { icon: Users, title: "Expert Consultations", desc: "Book sessions with domain experts for strategic guidance." },
              { icon: ClipboardCheck, title: "Track Progress", desc: "Save assessments, monitor scores, and benchmark over time." },
              { icon: Landmark, title: "Testbed", desc: "Co-create, test, and scale age-friendly solutions through living labs." },
            ].map((benefit, i) => (
              <motion.div
                key={benefit.title}
                className="rounded-xl bg-white border border-[#E2E8F0] p-5 text-center shadow-sm hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}
              >
                <benefit.icon className="mx-auto mb-3 text-[#6366F1]" size={28} strokeWidth={1.5} />
                <h3 className="font-bold text-[#0F172A] text-sm mb-1">{benefit.title}</h3>
                <p className="text-[#64748B] text-xs leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>

          <Button
            size="lg"
            className="bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold px-10 text-base shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate("/auth?mode=signup")}
          >
            Create Free Account
          </Button>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="relative py-28 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-[#6366F1]/10 rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          className="max-w-3xl mx-auto text-center relative z-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            Start Building Age-Friendly<br />Communities Today
          </h2>
          <p className="text-white/50 text-lg mb-10 max-w-lg mx-auto">From assessment to action — powered by AI.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold px-10 text-base shadow-[0_0_20px_rgba(99,102,241,0.4)] hover:shadow-[0_0_30px_rgba(99,102,241,0.6)] hover:scale-[1.02] transition-all duration-200"
              onClick={() => navigate("/auth?mode=signup")}
            >
              Create Free Account
            </Button>
            <Button size="lg" variant="ghost" className="border border-white/20 text-white bg-white/[0.06] backdrop-blur-sm hover:bg-white/[0.12] font-semibold px-10 text-base transition-all duration-200" onClick={() => navigate("/services")}>
              Explore Services
            </Button>
            <Button size="lg" variant="ghost" className="border border-white/20 text-white bg-white/[0.06] backdrop-blur-sm hover:bg-white/[0.12] font-semibold px-10 text-base transition-all duration-200" onClick={() => navigate("/marketplace")}>
              Browse Marketplace
            </Button>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
